import { AbortedError, NetworkError } from './errors';

export interface LatencyProfile {
  baseMs: number;
  jitterMs: number;
  failureRate: number; // 0..1
}

const DEFAULT_PROFILE: LatencyProfile = {
  baseMs: 100,
  jitterMs: 150,
  failureRate: 0,
};

let currentProfile: LatencyProfile = DEFAULT_PROFILE;

export function configureLatency(profile: Partial<LatencyProfile>): void {
  currentProfile = { ...currentProfile, ...profile };
}

export function getLatencyProfile(): LatencyProfile {
  return currentProfile;
}

/** Awaits simulated latency; rejects with NetworkError when failureRate hits. */
export function simulateLatency(signal?: AbortSignal): Promise<void> {
  const { baseMs, jitterMs, failureRate } = currentProfile;
  const delay = baseMs + Math.random() * jitterMs;

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortedError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      if (failureRate > 0 && Math.random() < failureRate) {
        reject(new NetworkError('simulated failure'));
      } else {
        resolve();
      }
    }, delay);

    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(new AbortedError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Read latency/failure knobs from the URL for demos:
 *   ?apiLatency=250  ?apiJitter=100  ?apiFailures=0.2
 */
export function applyLatencyFromQuery(searchParams: URLSearchParams): void {
  const patch: Partial<LatencyProfile> = {};
  const latency = searchParams.get('apiLatency');
  if (latency !== null && !Number.isNaN(Number(latency))) patch.baseMs = Number(latency);
  const jitter = searchParams.get('apiJitter');
  if (jitter !== null && !Number.isNaN(Number(jitter))) patch.jitterMs = Number(jitter);
  const failures = searchParams.get('apiFailures');
  if (failures !== null && !Number.isNaN(Number(failures))) patch.failureRate = Number(failures);
  if (Object.keys(patch).length > 0) configureLatency(patch);
}
